// Portal copy of the storefront plugin customizer contract.
//
// SOURCE OF TRUTH (keep in sync, separate repo):
//   storefront-plugin/src/theme/tokens.css  → THEME_TOKEN_GROUPS (every --wm-* token + default)
//   storefront-plugin/src/lib/labels.ts     → LABEL_SECTIONS / LABEL_FIELDS (LABEL_DEFS, LABEL_KEYS)
//
// Both live in `brand_plugin_config.theme`: tokens as `--wm-*` keys (CSS custom
// properties) and section text as `wm-label-*` keys (no `--`, so the bundle never
// emits them as CSS). Only non-default values are saved.

export type ThemeTokenType = 'color' | 'length' | 'font' | 'shadow';

export interface ThemeTokenDef {
    key: string;
    label: string;
    type: ThemeTokenType;
    /** tokens.css default. '' = unset (inherits the storefront font). */
    default: string;
    /** When the default is `var(--wm-x)`, the token it follows. */
    follows?: string;
    hint?: string;
}

export interface ThemeTokenGroup {
    id: string;
    title: string;
    tokens: ThemeTokenDef[];
}

// #region THEME_TOKEN_GROUPS
export const THEME_TOKEN_GROUPS: ThemeTokenGroup[] = [
    {
        id: 'colors',
        title: 'Colors',
        tokens: [
            { key: '--wm-color-primary', label: 'Primary', type: 'color', default: '#1a1a1a', hint: 'Selected cards; buttons follow it by default' },
            { key: '--wm-color-text', label: 'Text', type: 'color', default: '#1a1a1a' },
            { key: '--wm-color-muted', label: 'Muted text', type: 'color', default: '#6b6b6b', hint: 'Labels and secondary text' },
            { key: '--wm-color-border', label: 'Borders', type: 'color', default: '#d9d9d9', hint: 'Inputs, cards and table rules' },
            { key: '--wm-color-bg', label: 'Background', type: 'color', default: '#ffffff' },
            { key: '--wm-color-accent', label: 'Accent', type: 'color', default: '#8a6d3b', hint: 'Focus rings and "You Save"' },
            { key: '--wm-color-strike', label: 'Strikethrough price', type: 'color', default: '#9a9a9a' },
            { key: '--wm-color-error', label: 'Errors', type: 'color', default: '#b42318' },
        ],
    },
    {
        id: 'typography',
        title: 'Typography',
        tokens: [
            { key: '--wm-font-family', label: 'Font family', type: 'font', default: '', hint: 'Unset = your store font' },
            { key: '--wm-font-size', label: 'Base font size', type: 'length', default: '14px' },
            { key: '--wm-heading-size', label: 'Section heading size', type: 'length', default: '16px' },
        ],
    },
    {
        id: 'shape',
        title: 'Shape & Spacing',
        tokens: [
            { key: '--wm-radius', label: 'Corner radius', type: 'length', default: '6px' },
            { key: '--wm-spacing', label: 'Spacing', type: 'length', default: '12px' },
            { key: '--wm-shadow', label: 'Popover shadow', type: 'shadow', default: '0 4px 16px rgba(0, 0, 0, 0.12)' },
        ],
    },
    {
        id: 'buttons',
        title: 'Buttons',
        tokens: [
            { key: '--wm-button-bg', label: 'Button background', type: 'color', default: 'var(--wm-color-primary)', follows: '--wm-color-primary' },
            { key: '--wm-button-text', label: 'Button text', type: 'color', default: '#ffffff' },
            { key: '--wm-button-height', label: 'Button height', type: 'length', default: '48px' },
            { key: '--wm-button-radius', label: 'Button corner radius', type: 'length', default: 'var(--wm-radius)', follows: '--wm-radius' },
        ],
    },
    {
        id: 'badges',
        title: 'Badges & Swatches',
        tokens: [
            { key: '--wm-color-badge', label: '"Recommended" badge', type: 'color', default: 'var(--wm-color-accent)', follows: '--wm-color-accent' },
            { key: '--wm-color-badge-text', label: 'Badge text', type: 'color', default: '#ffffff' },
            { key: '--wm-color-placeholder', label: 'Image placeholder', type: 'color', default: '#efe9df', hint: 'Colorway swatch without an image' },
            { key: '--wm-swatch-size', label: 'Colorway swatch size', type: 'length', default: '40px' },
        ],
    },
];

export const THEME_TOKENS: ThemeTokenDef[] = THEME_TOKEN_GROUPS.flatMap((group: ThemeTokenGroup) => group.tokens);

// #region Labels
export const LABEL_PREFIX = 'wm-label-';
export const LABEL_MAX_LENGTH = 200;

export type LabelSection =
    | 'product_types' | 'colorways' | 'scale' | 'dimensions' | 'materials' | 'samples' | 'your_wall' | 'rolls' | 'cart';

/** Section groups in display order (storefront-plugin LABEL_SECTIONS). */
export const LABEL_SECTIONS: ReadonlyArray<{ section: LabelSection; title: string }> = [
    { section: 'product_types', title: 'Product type tabs' },
    { section: 'colorways', title: 'Colorways' },
    { section: 'scale', title: 'Scale' },
    { section: 'dimensions', title: 'Dimensions' },
    { section: 'materials', title: 'Materials' },
    { section: 'samples', title: 'Samples' },
    { section: 'your_wall', title: 'Your Wall' },
    { section: 'rolls', title: 'Roll lengths (wrapping paper)' },
    { section: 'cart', title: 'Cart' },
];

export interface LabelFieldDef {
    /** Theme key, e.g. `wm-label-colorways-title`. */
    key: string;
    label: string;
    default: string;
    section: LabelSection;
}

// [label key (storefront LABEL_DEFS), section, default text, customizer field name]
const LABEL_ROWS: ReadonlyArray<readonly [string, LabelSection, string, string]> = [
    ['product-type-wallpaper', 'product_types', 'Wallpaper', 'Wallpaper tab'],
    ['product-type-giftwrap', 'product_types', 'Wrapping Paper', 'Wrapping paper tab'],
    ['product-type-fabric', 'product_types', 'Fabric', 'Fabric tab'],

    ['colorways-title', 'colorways', 'Colorways', 'Title'],
    ['colorways-view', 'colorways', 'View this color', 'View link'],

    ['scale-title', 'scale', 'Choose Scale', 'Title'],
    ['scale-name', 'scale', 'Scale {scale}', 'Scale name'],
    ['scale-recommended', 'scale', 'Recommended', 'Recommended badge'],
    ['scale-repeat', 'scale', 'Repeat {repeat}', 'Repeat size'],

    ['dimensions-title', 'dimensions', 'Enter Wallpaper Dimensions', 'Title'],
    ['dimensions-width', 'dimensions', 'Width ({unit})', 'Width field'],
    ['dimensions-height', 'dimensions', 'Height ({unit})', 'Height field'],
    ['dimensions-width-error', 'dimensions', 'Enter a width greater than 0', 'Width error'],
    ['dimensions-height-error', 'dimensions', 'Height must be between {min} and {max} {unit}', 'Height error'],
    ['dimensions-printed', 'dimensions', 'Printed:', 'Printed size prefix'],
    ['dimensions-show-price', 'dimensions', 'Show Price and Material', 'Continue button'],

    ['materials-title', 'materials', 'Choose your material', 'Title'],
    ['materials-empty', 'materials', 'No materials available for this size', 'No materials message'],
    ['materials-unpasted', 'materials', 'Unpasted', 'Unpasted'],
    ['materials-peel-and-stick', 'materials', 'Peel & Stick', 'Peel & stick'],
    ['materials-roll', 'materials', 'Roll', 'Roll'],
    ['materials-sold-out', 'materials', 'Sold out', 'Sold out'],
    ['materials-per-sqft', 'materials', '{price}/sqft', 'Price per sq ft'],

    ['samples-title', 'samples', 'Try Sample', 'Title'],
    ['samples-from', 'samples', 'from {price}', 'Starting price'],
    ['samples-open', 'samples', 'Add +', 'Open button'],
    ['samples-add-one', 'samples', 'Add {count} sample to cart – {price}', 'Add button (1 sample)'],
    ['samples-add', 'samples', 'Add {count} samples to cart – {price}', 'Add button (several samples)'],

    ['your-wall-title', 'your_wall', 'Your Wall', 'Title'],
    ['your-wall-print', 'your_wall', 'Print', 'Print row'],
    ['your-wall-dimensions', 'your_wall', 'Dimensions', 'Dimensions row'],
    ['your-wall-material', 'your_wall', 'Material', 'Material row'],
    ['your-wall-scale', 'your_wall', 'Scale', 'Scale row'],
    ['your-wall-panels', 'your_wall', 'Panels', 'Panels row'],
    ['your-wall-area', 'your_wall', 'Area', 'Area row'],
    ['your-wall-rate', 'your_wall', 'Rate', 'Rate row'],
    ['your-wall-save', 'your_wall', 'You Save', 'Savings row'],
    ['your-wall-total', 'your_wall', 'Total', 'Total row'],
    ['your-wall-add', 'your_wall', 'Add to cart', 'Add to cart button'],

    ['rolls-title', 'rolls', 'Choose Roll Length', 'Title'],
    ['rolls-empty', 'rolls', 'No roll lengths available for this design', 'No rolls message'],
    ['rolls-length', 'rolls', '{length} ft roll', 'Roll length'],
    ['rolls-price', 'rolls', '{price} / roll', 'Price per roll'],
    ['rolls-sold-out', 'rolls', 'Sold out', 'Sold out'],
    ['rolls-quantity', 'rolls', 'Rolls', 'Quantity field'],
    ['rolls-add', 'rolls', 'Add to cart – {price}', 'Add to cart button'],
    ['rolls-choose', 'rolls', 'Choose a roll length', 'Choose prompt'],

    ['cart-adding', 'cart', 'Adding…', 'Adding to cart'],
];

export const LABEL_FIELDS: LabelFieldDef[] = LABEL_ROWS.map(([key, section, text, label]) => ({
    key: `${LABEL_PREFIX}${key}`,
    label,
    default: text,
    section,
}));

/** Every customizable key → its default (tokens.css value or label text). */
export const THEME_DEFAULTS: Readonly<Record<string, string>> = Object.freeze(Object.fromEntries([
    ...THEME_TOKENS.map((token: ThemeTokenDef) => [token.key, token.default]),
    ...LABEL_FIELDS.map((field: LabelFieldDef) => [field.key, field.default]),
]));

/** `{name}` placeholders in a label default, e.g. ['count', 'price']. */
export const labelPlaceholders = (text: string): string[] =>
    Array.from(text.matchAll(/\{(\w+)\}/g), (match: RegExpMatchArray) => match[1]);
